import React, { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { RowData } from "../../Interfaces/interfaces";
import VideoIdButtons from "../Buttons/videoIdButtons";
import { Toast } from "primereact/toast";
import "./tables.css";
import { first } from "lodash";
import { customThumbnailAuditLabel } from "../../Objects/filterOptions";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../../../config/tableLayout";

interface MetadataTableProps {
  rows: RowData[];
  loading: boolean;
  getCustomIDStatus: any;
  getPrivacy: any;
  lastUpdated: string | number | Date;
}

const MetadataTable: React.FC<MetadataTableProps> = ({
  rows,
  loading,
  getCustomIDStatus,
  getPrivacy,
  lastUpdated,
}) => {
  const [rowInFocus, setRowInFocus] = useState<number | null>(null);
  const toast = useRef<Toast>(null);
  const [isLoading, setIsLoading] = useState(true);
  const formatDateTime = (dateString: string | number | Date) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
      .format(date)
      .replace(",", "");
  };

  const formattedDate = lastUpdated ? formatDateTime(lastUpdated) : "N/A";

  const items = Array.from({ length: 5 }, (v, i) => i);
  const skeletonRows = items.map(() => ({}));
  useEffect(() => {
    if (!rows) {
      setIsLoading(true);
    } else if (rows.length > 0) {
      setIsLoading(false);
    }
  }, [rows]);

  const paginatorTemplate = {
    layout:
      "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown", // Add CurrentPageReport to the end
    CurrentPageReport: (options: any) => (
      <div className="flex align-items-center">
        <span
          style={{
            color: "var(--text-color)",
            userSelect: "none",
            textAlign: "center",
          }}
          className="mx-3"
        >
          Showing {options.first} to {options.last} of {options.totalRecords}{" "}
          entries
        </span>
      </div>
    ),
  };

  const lastUpdatedTemplate = () => (
    <div className="flex align-items-center">
      <text
        style={{
          color: "var(--text-color)",
          userSelect: "none",
          textAlign: "center",
        }}
        className="mx-3"
      >
        Last Updated: {formattedDate}
      </text>
    </div>
  );

  return (
    <>
      {isLoading === true ? (
        <DataTable
          value={skeletonRows}
          size="normal"
          paginator
          rows={50}
          paginatorTemplate={
            "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink  RowsPerPageDropdown"
          }
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries" // Changed the template
          paginatorLeft
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
          showGridlines
          cellSelection
          stripedRows
          removableSort
          scrollable
          scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
          sortMode="multiple"
          selectionMode="single"
          onCellClick={(e) => {
            setRowInFocus(e.rowIndex);
            navigator.clipboard.writeText(e.value);
            toast.current?.show({
              severity: "info",
              summary: "Copied to Clipboard",
              detail: e.value,
              life: 1000,
            });
          }}
          tableStyle={{ width: "auto", fontSize: "0.8rem" }}
          className="custom-datatable-paginator" // Add the custom class here
          style={{
            marginBottom: "-3.3rem",
            marginRight: "-1rem",
            marginLeft: "-1rem",
          }}
        >
          {/*create as many columns as needed*/}
          <Column field="videoId" header="Video ID" body={() => <Skeleton />} />
          <Column header="URL" body={() => <Skeleton />} />
          <Column header="Privacy" body={() => <Skeleton />} />
          <Column
            header="CMS"
            body={() => <Skeleton />}
            style={{ width: "10%" }}
          />
          <Column header="Content Format" body={() => <Skeleton />} />
          <Column header="Channel Name" body={() => <Skeleton />} />
          <Column header="Channel ID" body={() => <Skeleton />} />
          <Column
            header="Upload Date"
            body={() => <Skeleton />}
            style={{ width: "10%" }}
          />
          <Column
            header="Published Date"
            body={() => <Skeleton />}
            style={{ width: "10%" }}
          />
          <Column
            header="Scheduled Date"
            body={() => <Skeleton />}
            style={{ width: "10%" }}
          />
          <Column
            header="Channel Manager"
            body={() => <Skeleton />}
            style={{ width: "5%" }}
          />
          <Column header="Channel Manager Email" body={() => <Skeleton />} />
          <Column
            header="Video Title"
            body={() => <Skeleton />}
            style={{ width: "10%" }}
          />
          <Column
            header="Views"
            body={() => <Skeleton />}
            style={{ textAlign: "right" }}
          />
          <Column header="Custom ID" body={() => <Skeleton />} />
          <Column header="Description" body={() => <Skeleton />} />
          <Column header="Category" body={() => <Skeleton />} />
          <Column header="MFK Status" body={() => <Skeleton />} />
          <Column header="Custom Thumbnail" body={() => <Skeleton />} />
          {/* <Column header="Language Code" body={() => <Skeleton />} /> */}
          <Column header="Age Restriction" body={() => <Skeleton />} />
          <Column header="Paid Promo" body={() => <Skeleton />} />
          <Column header="License" body={() => <Skeleton />} />
        </DataTable>
      ) : (
        <DataTable
          value={rows.length > 0 ? rows : []}
          size="normal"
          paginator
          rows={50}
          paginatorTemplate={paginatorTemplate}
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          paginatorLeft={lastUpdatedTemplate()}
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
          showGridlines
          cellSelection
          stripedRows
          removableSort
          scrollable
          scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
          sortMode="multiple"
          selectionMode="single"
          onCellClick={(e) => {
            setRowInFocus(e.rowIndex);
            navigator.clipboard.writeText(e.value);
            toast.current?.show({
              severity: "info",
              summary: "Copied to Clipboard",
              detail: e.value,
              life: 1000,
            });
          }}
          tableStyle={{ width: "auto", fontSize: "0.8rem" }}
          className="custom-datatable-paginator"
          style={{
            marginBottom: "-3.3rem",
            marginRight: "-1rem",
            marginLeft: "-1rem",
          }}
        >
          {rows.length === 0 && (
            <tr>
              <td colSpan={Column.length} style={{ textAlign: "center" }}>
                No matching rows found.
              </td>
            </tr>
          )}
          <Column
            field="videoId"
            header="Video ID"
            headerTooltip="The unique YouTube ID that identifies the video"
            headerTooltipOptions={{
              position: "right",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          
          
          <Column
            header="URL"
            body={(rowData) => <VideoIdButtons rowData={rowData} />}
          />
          <Column
            field="privacy"
            header="Privacy"
            headerTooltip="The privacy status of the video"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            sortable
            body={(rowData) => {
              const backgroundColor =
                rowData.privacy === "Scheduled"
                  ? "orange"
                  : rowData.privacy === "Public"
                  ? "green"
                  : rowData.privacy === "Private"
                  ? "blue"
                  : "red";

              return (
                <div
                  style={{
                    backgroundColor,
                    color: "white",
                    textAlign: "center",
                    padding: "5px",
                    borderRadius: "4px",
                  }}
                >
                  {getPrivacy(rowData)}
                </div>
              );
            }}
          />

          <Column
            field="CMS"
            headerTooltip="The internal name of the YouTube CMS that the video and channel are connected to"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="CMS"
            style={{ width: "10%" }}
            sortable
            body={(rowData) => {
              if (rowData.CMS === "Missing") {
                return <span >{rowData.CMS}</span>;
              }
              return rowData.CMS || "";
            }}
            bodyClassName={(rowData: RowData) => {
              switch (rowData.CMS) {
                case "Missing":
                  return "incorrect";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="CreatorContentType"
            headerTooltip="Specifies if the content is a regular YouTube video, a Short or a Livestream"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Content Format"
            sortable
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="Channel_Name"
            headerTooltip="The name of the YouTube channel that the video is uploaded to"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Channel Name"
            sortable
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="YoutubeChannelId"
            headerTooltip="The unique YouTube ID that identifies the YouTube channel the video is uploaded to"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Channel ID"
            sortable
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="timeUploaded"
            headerTooltip="The date that the video was uploaded to YouTube (UTC)"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Upload Date"
            style={{ width: "10%" }}
            sortable
            body={(rowData) => {
              const date = new Date(rowData.timeUploaded);
              return isNaN(date.getTime())
                ? "Invalid Date"
                : date.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  });
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="timePublished"
            headerTooltip="The date that the video was published to YouTube (UTC)"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Published Date"
            style={{ width: "10%" }}
            sortable
            body={(rowData) => {
              const date = new Date(rowData.timePublished);
              return isNaN(date.getTime())
                ? "N/A"
                : date.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  });
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="PublishDate"
            headerTooltip="The date that the video is scheduled to be published to YouTube (UTC)"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Scheduled Date"
            style={{ width: "10%" }}
            sortable
            body={(rowData) => {
              const date = new Date(rowData.PublishDate);
              return isNaN(date.getTime())
                ? "N/A"
                : date.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  });
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="ChannelManagerName"
            headerTooltip="The name/s of the channel manager/s in charge of the YouTube Channel"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Channel Manager"
            sortable
            body={(rowData) => {
              if (rowData.ChannelManagerName === "Missing") {
                return <span>{rowData.ChannelManagerName}</span>;
              }

              if (
                rowData.ChannelManagerName &&
                rowData.ChannelManagerName.includes(",")
              ) {
                const emails = rowData.ChannelManagerName.split(",").map(
                  (email: string) => email.trim()
                );
                return <span>{emails.join("\n")}</span>;
              }
              return rowData.ChannelManagerName || "";
            }}
            bodyClassName={(rowData) => {
              switch (rowData.ChannelManagerName) {
                case "Missing":
                  return "status-cell incorrect";
                default:
                  return "";
              }
            }}
            style={{ width: "5%" }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="ChannelManagerEmail"
            headerTooltip="The email/s of the channel manager/s in charge of the YouTube Channel"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Channel Manager Email"
            sortable
            body={(rowData) => {
              if (rowData.ChannelManagerEmail === "Missing") {
                return <span>{rowData.ChannelManagerEmail}</span>;
              }

              if (
                rowData.ChannelManagerEmail &&
                rowData.ChannelManagerEmail.includes(",")
              ) {
                const emails = rowData.ChannelManagerEmail.split(",").map(
                  (email: string) => email.trim()
                );
                return <span>{emails.join("\n")}</span>;
              }
              return rowData.ChannelManagerEmail || "";
            }}
            bodyClassName={(rowData) => {
              switch (rowData.ChannelManagerEmail) {
                case "Missing":
                  return "status-cell incorrect";

                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="videoTitle"
            headerTooltip="The title of the video on YouTube"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Video Title"
            sortable
            body={(rowData) => {
              return <span>{rowData.videoTitle || "Missing"}</span>;
            }}
            bodyClassName={(rowData) => {
              switch (rowData.videoTitle) {
                case "Missing":
                  return "status-cell incorrect";

                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
            style={{ width: "10%" }}
          />
          <Column
            field="views"
            headerTooltip="The total number of views that the video has generated in its entire lifetime"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Views"
            sortable
            style={{ textAlign: "right" }}
            body={(rowData) => rowData.views.toLocaleString()}
          />
          <Column
            field="customid"
            headerTooltip="The internal code assigned to the video by the channel manager to identify it in the catalog, and status checking whether it was set correctly"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Custom ID"
            sortable
            body={(rowData) => {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {" "}
                  {/* Centering container */}
                  <span
                    style={{
                      textAlign: "center", // Center the text within the span
                      paddingBottom: "10px",
                    }}
                  ></span>
                  <span style={{ textAlign: "center" }}>
                    {" "}
                    {/* Center the text within the span */}
                    {rowData.customid}
                  </span>
                </div>
              );
            }}
            bodyClassName={(rowData) => {
              switch (getCustomIDStatus(rowData)) {
                case "Incorrect":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />

          <Column
            field="Description"
            headerTooltip="Checks whether a YouTube video has a description."
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Description"
            sortable
            body={(rowData) => <span         
            >{rowData.Description}</span>}
            bodyClassName={(rowData) => {
              switch (rowData.Description) {
                case "Missing or under 100 characters":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="category"
            header="Category"
            headerTooltip="Checks whether a YouTube video has been set with a correct category."
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            sortable
            body={(rowData) => {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {" "}
                  {/* Centering container */}
                  <span
                    style={{
                      textAlign: "center", // Center the text within the span
                      paddingBottom: "10px",
                    }}
                  ></span>
                  <span style={{ textAlign: "center" }}>
                    {" "}
                    {/* Center the text within the span */}
                    {rowData.category}
                  </span>
                </div>
              );
            }}
            bodyClassName={(rowData) => {
              switch (rowData.categoryStatus) {
                case "Incorrect":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="mfk"
            header="MFK Status"
            headerTooltip="Checks whether a YouTube video has been set to MFK."
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            sortable
            body={(rowData) => {
              return <span>{rowData.mfk}</span>;
            }}
            bodyClassName={(rowData) => {
              switch (rowData.mfk) {
                case "No":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />

          <Column
            field="CustomThumbnail"
            header="Custom Thumbnail"
            headerTooltip="Checks whether a YouTube video has a custom thumbnail."
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            sortable
            body={(rowData) => {
              return <span>{customThumbnailAuditLabel(rowData)}</span>;
            }}
            bodyClassName={(rowData) => {
              switch (customThumbnailAuditLabel(rowData)) {
                case "No":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          {/* <Column
            field="LanguageCode"
            header="Language Code"
            headerTooltip="Checks whether a YouTube video and the Catalog have a matching language."
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            sortable
            body={(rowData) => {
              return <span>{rowData.LanguageCode}</span>;
            }}
            bodyClassName={(rowData) => {
              switch (rowData.LanguageCode) {
                case "Incorrect":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          /> */}
          <Column
            field="ageRestriction"
            header="Age Restriction"
            headerTooltip="Checks whether a YouTube video has any age restrictions."
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            sortable
            body={(rowData) => {
              return <span>{rowData.ageRestriction}</span>;
            }}
            bodyClassName={(rowData) => {
              switch (rowData.ageRestriction) {
                case "Yes":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="promotion"
            header="Paid Promo"
            headerTooltip="Checks whether a YouTube video has any promotional content."
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            sortable
            body={(rowData) => {
              return <span>{rowData.promotion}</span>;
            }}
            bodyClassName={(rowData) => {
              switch (rowData.promotion) {
                case "Yes":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="license"
            header="License"
            headerTooltip="Checks whether a YouTube video is set with a correct license."
            headerTooltipOptions={{
              position: "left",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            sortable
            body={(rowData) => {
              return (
                <span
                  style={{
                    color:
                      rowData.license === "Creative Common"
                        ? "red"
                        : rowData.license === "Whitelisted"
                        ? "blue"
                        : "inherit",
                  }}
                >
                  {rowData.license}
                </span>
              );
            }}
            bodyClassName={(rowData) => {
              switch (rowData.license) {
                case "Incorrect":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
        </DataTable>
      )}
      <Toast ref={toast} />
    </>
  );
};

export default MetadataTable;
